<template>
    <div
        ref="switcherRef"
        class="ui-tab-switcher _scrollbar"
        :class="{
            'ui-tab-switcher_premium': color === 'premium',
            'ui-tab-switcher_private': color === 'private',
            'ui-tab-switcher_white': color === 'white',
            'ui-tab-switcher_black': color === 'black',
            'ui-tab-switcher_dark': color === 'dark',
            'ui-tab-switcher_focused': isFocused,
        }"
    >
        <div
            ref="container"
            class="ui-tab-switcher__container"
        >
            <div
              class="ui-tab-switcher__tile"
              :style="tileStyle"
            />
            <div
                v-for="item in items"
                :key="item.value"
                ref="itemEls"
                class="ui-tab-switcher__item"
                :class="{
                    'ui-tab-switcher__item_active': item.value === localValue,
                }"
                :data-value="item.value"
            >
                <input
                    v-model="localValue"
                    type="radio"
                    :name="name"
                    :id="`${name}-${item.value}`"
                    :value="item.value"
                    @focus="isFocused = true"
                    @blur="isFocused = false"
                >
                <label class="ui-tab-switcher__label" :for="`${name}-${item.value}`">
                    <span>{{ item.label }}</span>
                </label>
            </div>
        </div>
    </div>
</template>
<script>
import { ref, computed, watch } from 'vue';
import { getRect } from '../../assets/js/vue/utlis/getRect';
import { useResize } from '../../assets/js/vue/utlis/useResize';

export default {
    name: 'UiTabSwitcher',
    setup() {
        const localValue = ref(false);
        const activeElementWidth = ref(0);
        const activeElementLeft = ref(0);
        const itemEls = ref(null);
        const container = ref(null);
        const switcherRef = ref(null);

        const activeEl = computed(() => {
            return itemEls.value?.find((el) => el.dataset.value === localValue.value);
        });


        const rafHandle = async () => {
            if (!activeEl.value || !container.value) return;

            const [{ left, width , right }, { left: containerLeft }, { right: switcherRight, left: switcherLeft }] = await Promise.all([
                getRect(activeEl.value),
                getRect(container.value),
                getRect(switcherRef.value),
            ]);

            activeElementWidth.value = Math.floor(width);
            activeElementLeft.value = Math.floor(left - containerLeft);

            const rightOffset = switcherRight - right;
            const leftOffset = left - switcherLeft;

            if (rightOffset < 0) switcherRef.value.scrollTo({
                left: switcherRef.value.scrollLeft - rightOffset,
                behavior: 'smooth',
            });
            if (leftOffset < 0) switcherRef.value.scrollTo({
                left: switcherRef.value.scrollLeft + leftOffset,
                behavior: 'smooth',
            });
        };

        watch(activeEl, rafHandle);

        useResize(rafHandle, true);

        return {
            localValue,
            activeElementWidth,
            activeElementLeft,
            itemEls,
            activeEl,
            container,
            switcherRef,
        };
    },
    props: {
        /**
         * @typedef ListItem
         * @property {string} value
         * @property {string} label
         */

        /**
         * @type {ListItem[]}
         */
        items: { type: Array, default: () => []},
        modelValue: String,
        color: {
            type: String,
            default: 'white',
            validator: (val) => ['white', 'black', 'dark', 'premium', 'private'].includes(val),
        },
    },
    data() {
        return {
            isFocused: false,
        };
    },
    computed: {
        name() {
            return `ui-tab-switcher-${this._uid}`;
        },
        tileStyle() {
            return {
                width: `${this.activeElementWidth}px`,
                left: `${this.activeElementLeft}px`,
            }
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(val) {
                this.localValue = val ?? this.items?.[0]?.value;
            },
        },
        localValue(val) {
            if (val !== this.modelValue) this.$emit('update:modelValue', val);
        },
    },
};
</script>
<style lang="scss">
@import "/local/templates/redesign/src/assets/scss/base/_variables.scss";
@import "/local/templates/redesign/src/assets/scss/base/_mixins.scss";
@import "/local/templates/redesign/src/assets/scss/base/_functions.scss";

.ui-tab-switcher {
    --bg-color: #{$white-101};
    --bg-tile: #{$grey-109};
    --color-active: #{$grey-1514};
    --color: #{$grey-1514};
    --tile-shadow-active: transparent;
    --border-color: #{$grey-166};
    @include fluid('--py', 13px, 11px);
    @include fluid('--px', 32px, 24px);
    user-select: none;
    color: var(--color);
    overflow-x: auto;
    overflow-y: hidden;
    max-width: 100%;
    @include fluid('height', 56px, 48px);

    display: inline-block;

    &_focused {
        --tile-active-shadow-opacity: 0.3;
    }

    /**
     * TODO: add styles for other color variants
     */
    &_black {
        --bg-color: #{$grey-106};
        --bg-tile: #{$grey-1514};
        --color-active: #{$white-101};
        --color: #{$grey-1514};
    }
    &_dark {
        --bg-color: transparent;
        --bg-tile: #{$grey-868};
        --color-active: #{$white-101};
        --color: #{$grey-166};
    }

    &__container {
        position: relative;
        @include fluid('height', 48px, 42px);
        @include fluid('margin-top', 4px, 3px);
        box-shadow: inset 0 0 0 1px var(--border-color);
        @include fluid('border-radius', 20px);
        background-color: var(--bg-color);
        display: flex;
        width: fit-content;
    }

    &__item {
        position: relative;
        @include fluid('height', 48px, 42px);
        @include fluid('font-size', 16px, 14px);
        @include fluid('line-height', 22px, 20px);
        transition: color .2s;
        overflow: hidden;

        & input {
            margin: 0;
            width: 0;
            height: 0;
        }

        & label {
            display: block;
            padding: var(--py) var(--px);
            cursor: pointer;
            white-space: nowrap;
        }

        &_active {
            color: var(--color-active);
        }
    }

    &__tile {
        position: absolute;
        box-sizing: content-box;
        top: 0;
        @include fluid('height', 48px, 42px);
        @include fluid('border-radius', 16px);
        background-color: var(--bg-tile);
        transform-origin: center;
        transition: left .2s, width .2s, box-shadow .2s;
        padding: var(--tile-py) 0;
        @include fluid('--tile-py', 4px, 3px);
        transform: translateY(calc(var(--tile-py) * -1));

        &::after {
            content: "";
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            @include fluid('border-radius', 16px);
            box-shadow: 0px 4px 12px 0px var(--bg-tile);
            opacity: var(--tile-active-shadow-opacity, 0);
            display: block;
            position: absolute;
            transition: opacity .3s;
        }
    }
}
</style>
